import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import MetricCard from '../../components/MetricCard';
import accountApi from '../../services/accountApi';

function OverviewPage() {
  const { activeTenant, activeTenantId, user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeTenantId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await accountApi.getPartnerOverview(activeTenantId);
        if (!cancelled) setOverview(data);
      } catch (err) {
        if (!cancelled) setOverview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const partnerName =
    activeTenant?.partner_name || activeTenant?.tenant_name || 'Partner';
  const counts = overview?.counts || null;

  return (
    <div>
      <h1 className="product-page-title">Partner Overview</h1>
      <p className="product-page-subtitle">
        Welcome{user?.display_name ? `, ${user.display_name}` : ''}.
      </p>
      <div className="product-card mb-3">
        {activeTenant ? (
          <>
            <h2 className="h5 mb-3">{partnerName}</h2>
            <p className="mb-2">
              <strong>Role:</strong> {activeTenant.role}
            </p>
            <p className="mb-0 text-muted">
              Operational snapshot for projects, bake jobs, team, and Partner API access.
            </p>
          </>
        ) : (
          <p className="mb-0">
            No active partner. Create a partner or ask an administrator for an invitation.
          </p>
        )}
      </div>

      {loading && (
        <div className="mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading partner metrics…
        </div>
      )}

      {counts && (
        <div className="row g-3 mb-3">
          {[
            ['Projects', counts.projects, '/console/projects'],
            ['Queued jobs', counts.queued_jobs, '/console/jobs'],
            ['Running jobs', counts.running_jobs, '/console/jobs'],
            ['Failed jobs', counts.failed_jobs, '/console/jobs'],
            ['Compatibility rejects', counts.compatibility_rejections, '/console/projects'],
            ['Artifacts', counts.artifacts, '/console/artifacts'],
            ['Team members', counts.members, '/console/team'],
            ['Partner API keys', counts.api_keys, '/console/api-keys'],
          ].map(([label, value, href]) => (
            <div className="col-6 col-md-3" key={label}>
              <MetricCard label={label} value={value} href={href} />
            </div>
          ))}
        </div>
      )}

      {overview?.next_actions?.length > 0 && (
        <div className="product-card mb-3">
          <h2 className="h6">Recommended next actions</h2>
          <div className="d-flex flex-wrap gap-2">
            {overview.next_actions.map((a) => (
              <Link key={a.href + a.label} to={a.href} className="btn btn-outline-primary btn-sm">
                {a.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {overview?.recent_audit?.length > 0 && (
        <div className="product-card mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="h6 mb-0">Recent activity</h2>
            <Link to="/console/activity" className="small">
              View all
            </Link>
          </div>
          <ul className="mb-0 ps-3">
            {overview.recent_audit.slice(0, 5).map((e) => (
              <li key={e.id} className="small">
                <span className="mono">{e.action}</span>
                {e.actor_email ? ` · ${e.actor_email}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeTenant && !overview?.next_actions?.length && (
        <div className="d-flex flex-wrap gap-2">
          <Link to="/console/projects" className="btn btn-outline-primary btn-sm">
            Projects
          </Link>
          <Link to="/console/jobs" className="btn btn-outline-primary btn-sm">
            Bake Jobs
          </Link>
          <Link to="/console/team" className="btn btn-outline-primary btn-sm">
            Team
          </Link>
        </div>
      )}
    </div>
  );
}

export default OverviewPage;
